/**
 * Utility functions for managing order and product related business logic.
 * This file contains functions that help in formatting values,
 * determining visibility of sections based on data received from backend,
 * and any other order-specific logic that may be needed.
 */
import { JeopardyCategories, ProductDefaultValues, ProductJeopardyCategoryMessage } from './constants'
import { IActivity, IAppointmentDetails, IEquipmentDetails, IJeopardyActionMessage, IOrderDetails,
  IProductOrder} from '@applications/order-tracker/config/order-tracker-new-common/types';

export const showOrderDetailsComponent = (orderSummary) => {
    if (orderSummary?.telstraProjectCode &&
        ((orderSummary?.isLegacy) && orderSummary?.secondaryReference)) {
        return true;
    }
    return false;
}

export const showSecReference = (orderSummary) => {
    if (!orderSummary?.telstraProjectCode &&
        ((orderSummary?.isLegacy) && orderSummary?.secondaryReference)) {
        return true;
    }
    return false;
}

export const showTelstraProjectCode = (orderSummary) => {
    if (orderSummary?.telstraProjectCode && (!orderSummary?.isLegacy)) {
        return true;
    }
    return false;
}

export const showTelstraProjectCodeLegacy = (orderSummary) => {
    if (orderSummary?.telstraProjectCode && (orderSummary?.isLegacy
        && !orderSummary?.secondaryReference)) {
        return true;
    }
    return false;
}

export const showOrderDetailsSection = (orderSummary) => {

    if (orderSummary.telstraProjectCode || ((orderSummary.isLegacy) && orderSummary.secondaryReference)) {
       return true;
    }

    return false;
}

export const showOrderContactsSection = (orderSummary: IOrderDetails): boolean => {
    // Edge case where none of the details are not provided.
    if (!orderSummary.telstraProjectCode && (orderSummary.isLegacy && !orderSummary.secondaryReference) &&
        !orderSummary.projectManagerContact && !orderSummary.telstraConsultantContact &&
        !orderSummary.customerTechnicalContact && !orderSummary.customerProjectContact) {
        return false;
    }

    // Telstra Project code not present but some contact details are present.
    if ((!orderSummary.telstraProjectCode && (orderSummary.isLegacy && !orderSummary.secondaryReference)) &&
        orderSummary.projectManagerContact || orderSummary.telstraConsultantContact ||
        orderSummary.customerTechnicalContact || orderSummary.customerProjectContact) {
        return true;
    }

    if (orderSummary.telstraProjectCode || ((orderSummary.isLegacy) && orderSummary.secondaryReference)) {
        return true;
    }

    return false;
}

// Validates product fields and provides default values if necessary.
export const validateProductFields = () => {
  const validateProductField = (value: string | string[], defaultValue: string) =>  {
    if (Array.isArray(value)) {
      return value.filter((field: string) => field).length ? value : defaultValue;
    } else {
      return value || defaultValue;
    }
  }
  return {
        validateSiteName: (value: string) => validateProductField(value, ProductDefaultValues.NotApplicable),
        validateEstimatedCompletionDate: (value: string): boolean => Boolean(value),
        validateUpcomingAppointment: (value: string) => validateProductField(value, ProductDefaultValues.NotApplicable),
        validateServiceId: (value: string) => validateProductField(value, ProductDefaultValues.NotApplicable),
        validateSiteAddress: (value: string) => validateProductField(value?.replace(/,/g, ', '), ProductDefaultValues.SiteAddress),
        validateContact: (value: string[]) => validateProductField(value, ProductDefaultValues.NotApplicable),
    }
}

// Business rules for showing various equipment details based on the provided equipment details.
export const productEquipmentDetailsBusinessRules = (
  equipmentDetails: IEquipmentDetails
) => {
  return {
    // Determine if product equipment details section should be shown.
    showEquipmentDetailsSection: (): boolean =>
      Boolean(equipmentDetails?.consignmentNumber),
    // Determine if the delivery status should be displayed.
    showDeliveryStatus: (): boolean =>
      Boolean(equipmentDetails?.deliveryStatus),
    // Determine if the estimated delivery date should be displayed.
    showDeliveryDate: (): boolean =>
      Boolean(equipmentDetails?.deliveryDate),
    // Determine if the track consignment link should be displayed.
    showTrackConsignmentLink: (): boolean =>
      Boolean(equipmentDetails?.trackingURL),
  };
};

// Business rules for showing various appointment information based on the provided appointment details.
export const productAppointmentInformationBusinessRules = (
    appointment: IAppointmentDetails
  ) => {
    return {
      // Determine if product appointment information section should be shown.
      showAppointmentInformationSection: (): boolean =>
        Boolean(appointment?.date),
      // Determine if the appointment status should be displayed.
      showAppointmentStatus: (): boolean =>
        Boolean(appointment?.status),
    };
};

// Business rules for Product Jeopardy Category Message Section
export const productJeopardyCategoryMessageBusinessRules = (
  activity: IActivity
) => {
  // Determine if the activity's jeopardy category indicates action is required
  const isActionRequired: boolean =
    activity?.jeopardyCategory?.toLocaleLowerCase() ===
    JeopardyCategories.ActionRequired.toLocaleLowerCase();

  // Function to create the jeopardy action message based on the category
  const createJeopardyActionMessage = (): IJeopardyActionMessage => {
    return {
      title: isActionRequired
        ? JeopardyCategories.ActionRequired
        : JeopardyCategories.NoActionRequired,
      description: isActionRequired
        ? ProductJeopardyCategoryMessage[JeopardyCategories.ActionRequired]
        : ProductJeopardyCategoryMessage[JeopardyCategories.NoActionRequired],
      isWarning: isActionRequired,
    };
  };

  return {
    // Generate the jeopardy action message immediately upon calling this function
    jeopardyActionMessage: createJeopardyActionMessage(),
  };
};

// Business rules for showing various product details based on the provided product details.
export const productDetailsBusinessRules = (product: IProductOrder) => {
  return {
      // Determine if project manager details should be shown.
      showProjectManagerDetails: () =>
          Boolean(product?.projectManagerContact && product?.telstraProjectCode),
      // Determine if Telstra consultant contact details should be shown.
      showTelstraConsultantContact: () =>
          Boolean(product?.telstraConsultantContact && product?.telstraProjectCode),
      // Determine if billing account section should be shown.
      showBillingAccountNumber: () =>
          Boolean(product?.billingAccountNumber),
      // Determine if estimated completion section should be shown.
      showEstimatedCompletion: () =>
          Boolean(product?.estimatedCompletionDate && product?.productProgress !== 'Completed' && product?.productProgress !== 'Closed')
  };
};

// Business rules for showing various progress tracker status based on the order action type.
export const progressTrackerBusinessRules = (product: IProductOrder) => {
  const activityType = product?.activityType;
  return {
    showProgressTrackerStages: () => Boolean(
      activityType?.trim() &&
      product?.progressTrackerActivities?.length
    ),
  };
};
